import {
  getBusinessProcessDefinition,
  type BusinessProcessShapeNames,
} from '../businessProcess/definitions';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import { type TextProps } from '../objects/EmbeddedTextBehaviorsV2';
import type Shape from '../objects/Shape';
import { ToolBorderColorWithOpacity } from '../tools/ToolBorderColor';
import {
  ToolBusinessProcessCategory,
  ToolBusinessProcessType,
} from '../tools/businessProcessTools';
import { ToolFillColor } from '../tools/ToolFillColor';
import ToolFontSize from '../tools/ToolFontSize';
import ToolTextColor from '../tools/ToolTextColor';
import { ToolTextFormat } from '../tools/ToolTextFormat';
import { ToolTextLocationOutside } from '../tools/ToolTextLocation';
import { type Constructor, type PreparedIcon } from '../types';
import { getPreparedIcon } from '../utils/icons';
import PolygonBase from './PolygonBase';
import ShapeBase from './ShapeBase';

export function TextLabelBehaviors<T extends Constructor<BusinessProcessBase>>(
  Base: T
): T {
  return class extends Base {
    static defaultProperties = {
      // @ts-expect-error this is fine, but TS doesn't like it cos it forces us to use the
      // Constructor type instead of typeof BusinessProcessBase above
      ...Base.defaultProperties,
      textLocation: 'below',
    };

    textProps: TextProps = {
      ...ShapeBase.defaultTextProps,
      location: 'user',
      wrapText: 'growText',
      margins: 'noScale',
    };

    hasEmbeddedText() {
      return true;
    }

    calcTextMargin() {
      return {
        top: 5,
        left: 0,
        right: 0,
        bottom: 0,
      };
    }

    textTools() {
      return [
        [ToolTextLocationOutside, ToolTextColor, ToolTextFormat, ToolFontSize],
      ];
    }
  };
}

class BusinessProcessBase extends PolygonBase {
  static shapeControls = this.createDefaultControls({ noRotate: true });

  static defaultProperties = {
    stroke: '#3C3C3C',
    fill: '#FFFFFF',
    textContent: '<p>Double-click to edit</p>',
  };

  uniformScaling = true;

  icon: PreparedIcon | undefined;
  secondaryIcon: PreparedIcon | undefined;

  constructor(fabricObject: Shape) {
    super(fabricObject);

    this.prepareIcons();
  }

  wasSet(keyOrPath: KeyPath, value: unknown) {
    super.wasSet(keyOrPath, value);

    if (
      keyOrPath === 'variant' ||
      keyOrPath === 'shapeName' ||
      keyOrPath === 'stroke'
    ) {
      this.prepareIcons();
    }
  }

  prepareIcons() {
    const definition = this.getDefinition();
    if (!definition) {
      return;
    }
    const { iconName, secondaryIconName } = definition.properties ?? {};

    // prepare the icons for rendering
    for (const [attr, name] of [
      ['icon', iconName],
      ['secondaryIcon', secondaryIconName],
    ] as const) {
      if (name) {
        void getPreparedIcon(name, this.stroke ?? '#333333')
          .then(icon => {
            this[attr] = icon;
            this.canvas?.requestRenderAll();
          })
          .catch(e => {
            console.error('Error decoding image', e);
          });
      }
    }
  }

  getDefinition() {
    const shapeName = this.fabricObject.shapeName as BusinessProcessShapeNames;
    const variant = this.fabricObject.variant;
    return getBusinessProcessDefinition(shapeName, variant!);
  }

  getBorderStyle() {
    const definition = this.getDefinition();
    return definition?.properties?.borderStyle ?? 'regular';
  }

  getObjectProperties() {
    const borderStyle = this.getBorderStyle();

    const strokeDouble =
      borderStyle === 'double' || borderStyle === 'double-dashed';
    const strokeWidth =
      {
        thick: 6,
        double: 9,
        'double-dashed': 9,
      }[borderStyle] ?? 3;

    return {
      minScaleLimit: 1,
      uniformScaling: this.uniformScaling,
      strokeWidth,
      strokeDouble,
    };
  }

  tools() {
    return [
      [ToolBusinessProcessCategory, ToolBusinessProcessType],
      [ToolFillColor, ToolBorderColorWithOpacity],
    ];
  }

  hasEmbeddedText() {
    return false;
  }

  getBorderDash() {
    const borderStyle = this.getBorderStyle();

    return borderStyle === 'dashed' || borderStyle === 'double-dashed'
      ? [12, 3]
      : [];
  }
}

export default BusinessProcessBase;
